/**
 * RendPleX Scenario Analyzer 2.0 — assumption sourcing.
 *
 * Single place that decides what goes into a scenario snapshot. The two
 * sources are:
 *   - "persisted_deal": a Deal loaded via GET /api/deals/{deal_id}. All
 *     fields present on the deal (including explicit zeros) are honored
 *     verbatim. Only fields that are genuinely absent (undefined/null)
 *     fall back to the module defaults.
 *   - "defaults": freehand mode. Every field comes from DEFAULT_SCENARIO.
 *
 * The snapshot returned by buildScenario() is the seed for BOTH the
 * immutable Original Deal AND the initial Current Scenario. Consumers
 * must clone it (structuredClone or deep spread) before mutating.
 */

export const DEFAULT_SCENARIO = Object.freeze({
  purchase_price: 750000,
  num_units: 1,
  annual_income: 36000,          // $3,000/mo × 12
  annual_property_tax: 9000,     // 1.2% of default price
  down_payment_percent: 20,
  interest_rate: 5.5,
  loan_term_years: 25,
  annual_insurance: 3750,        // 0.5% of default price
  monthly_maintenance: 100,
  utilities_electricity: 0,
  utilities_heating: 0,
  utilities_water: 0,
  utilities_sewer: 0,
  utilities_internet: 0,
  utilities_cable: 0,
  vacancy_rate: 5,
  property_management_percent: 8,
  repairs_reserve_annual: 0,
  snow_removal_annual: 0,
  landscaping_annual: 0,
  cleaning_common_annual: 0,
  security_alarm_annual: 0,
  garbage_waste_annual: 0,
});

// Fields that must be preserved when hydrating from a saved Deal.
// Explicit zero on any of these is a real value, not "missing".
const HYDRATED_FIELDS = Object.keys(DEFAULT_SCENARIO);

/**
 * Build a scenario snapshot from either a persisted Deal or nothing.
 * Explicit zero values on a persisted Deal are preserved.
 *
 * @param {object|null|undefined} deal — result of GET /api/deals/{id}
 * @returns {{ scenario: object, source: 'persisted_deal' | 'defaults' }}
 */
export function buildScenario(deal) {
  if (!deal || typeof deal !== 'object') {
    return { scenario: { ...DEFAULT_SCENARIO }, source: 'defaults' };
  }
  const scenario = { ...DEFAULT_SCENARIO };
  for (const key of HYDRATED_FIELDS) {
    // Only fall back when the field is genuinely absent.
    // undefined and null are absent; 0 is a real value.
    const v = deal[key];
    if (v !== undefined && v !== null) {
      scenario[key] = v;
    }
  }
  // property_address is metadata, not a financial field.
  scenario.property_address = deal.property_address || '';
  return { scenario, source: 'persisted_deal' };
}

/** Deep-copy a scenario. Used to derive Current Scenario from Original. */
export function cloneScenario(scenario) {
  return { ...scenario };
}

/** Down payment presets shown in the UI. Solver may search beyond these. */
export const DOWN_PAYMENT_PRESETS = [20, 25, 30, 35];

/** Target cash-flow presets ($/month). */
export const TARGET_CASH_FLOW_PRESETS = [0, 250, 500, 1000];

/** Solver variable enum shared with the backend contract. */
export const SOLVE_FOR_OPTIONS = [
  'purchase_price',
  'rent',
  'down_payment',
  'interest_rate',
];
